import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TextField,
  Button,
  MenuItem,
} from "@mui/material";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";

const COMPANY_OPTIONS = [
  "บริษัท ณสุ จำกัด (Nasu)",
  "บริษัท สแควร์ไมล์กรุ๊ป จำกัด (SQM)",
];

export default function MonthlyCompanyPurchaseReport() {
  // ---------------- MONTH ----------------
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();

    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  // ---------------- DATE RANGE ----------------
  const startDate = `${selectedMonth}-01`;

  const endDate = new Date(
    Number(selectedMonth.split("-")[0]),
    Number(selectedMonth.split("-")[1]),
    0,
  )
    .toISOString()
    .split("T")[0];

  // ---------------- FILTER ----------------
  const [companyFilter, setCompanyFilter] = useState("all");

  // ---------------- DATA ----------------
  const [loading, setLoading] = useState(false);

  const [rows, setRows] = useState([]);

  const cellStyle = {
    border: "1px solid #ddd",
    whiteSpace: "nowrap",
  };

  // ---------------- FETCH ----------------
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const res = await services.getPurchaseTransactions({
          status: "payed",
          startDate,
          endDate,
        });

        const rawData = res?.data?.data ?? res?.data ?? [];

        setRows(Array.isArray(rawData) ? rawData : []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  // ---------------- FORMAT ----------------
  const formatNum = (val) =>
    Number(val || 0).toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatThaiDate = (dateStr) => {
    const d = new Date(dateStr);

    return d.toLocaleDateString("th-TH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // ---------------- DATASET ----------------
  const dataset = useMemo(() => {
    const data = [];

    rows.forEach((row) => {
      // FILTER COMPANY
      if (companyFilter !== "all" && row.companyName !== companyFilter) {
        return;
      }

      (row.items || []).forEach((item) => {
        data.push({
          date: row.createdAt,

          companyName: row.companyName || "-",

          productName: item.productName || item.product?.title || "-",

          price: Number(item.pricePerUnit || 0),

          unit: item.unit || "กก.",

          qty: Number(item.netWeight || 0),

          total: Number(item.netPrice || 0),

          batch: row.billNumber || "-",
        });
      });
    });

    data.sort((a, b) => new Date(a.date) - new Date(b.date));

    return data;
  }, [rows, companyFilter]);

  // ---------------- SUMMARY ----------------
  const summary = useMemo(() => {
    return dataset.reduce(
      (acc, item) => {
        acc.qty += item.qty;
        acc.total += item.total;

        return acc;
      },
      {
        qty: 0,
        total: 0,
      },
    );
  }, [dataset]);

  // ---------------- EXPORT CSV ----------------
  const exportToCSV = () => {
    if (!dataset.length) return;

    const headers = [
      "วันที่",
      "ชื่อสินค้า",
      "ราคา",
      "หน่วยนับ",
      "รวม",
      "มูลค่ารวม",
      "หมายเหตุ",
    ];

    const rowsCSV = dataset.map((item) => [
      formatThaiDate(item.date),
      item.productName,
      item.price.toFixed(2),
      item.unit,
      item.qty.toFixed(2),
      item.total.toFixed(2),
      item.batch,
    ]);

    const csvContent = [
      headers.join(","),
      ...rowsCSV.map((r) => r.map((v) => `"${v}"`).join(",")),
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);

    link.download = `monthly_company_purchase_${selectedMonth}.csv`;

    link.click();
  };

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
      }}
    >
      <AppBreadcrumbs />

      {/* FILTER */}
      <Box display="flex" justifyContent="flex-end" gap={2} my={2} flexWrap="wrap">
        {/* MONTH */}
        <TextField
          label="เดือน"
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />

        {/* COMPANY */}
        <TextField
          select
          label="บริษัทผู้จ่าย"
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          sx={{
            minWidth: 320,
          }}
        >
          <MenuItem value="all">ทั้งหมด</MenuItem>

          {COMPANY_OPTIONS.map((company) => (
            <MenuItem key={company} value={company}>
              {company}
            </MenuItem>
          ))}
        </TextField>

        {/* EXPORT */}
        <Button
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={exportToCSV}
        >
          Export CSV
        </Button>
      </Box>

      {/* LOADING */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="h6" gutterBottom>
            รายงานรายเดือนแยกตามผู้จ่ายเงิน
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              {/* HEADER */}
              <TableHead
                sx={{
                  backgroundColor: "#f0f0f0",
                }}
              >
                <TableRow>
                  <TableCell sx={cellStyle}>วันที่</TableCell>

                  <TableCell sx={cellStyle}>ชื่อสินค้า</TableCell>

                  <TableCell align="right" sx={cellStyle}>
                    ราคา
                  </TableCell>

                  <TableCell sx={cellStyle}>หน่วยนับ</TableCell>

                  <TableCell align="right" sx={cellStyle}>
                    รวม
                  </TableCell>

                  <TableCell align="right" sx={cellStyle}>
                    มูลค่ารวม
                  </TableCell>

                  <TableCell sx={cellStyle}>หมายเหตุ</TableCell>
                </TableRow>
              </TableHead>

              {/* BODY */}
              <TableBody>
                {dataset.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={cellStyle}>
                      {formatThaiDate(item.date)}
                    </TableCell>

                    <TableCell sx={cellStyle}>{item.productName}</TableCell>

                    <TableCell align="right" sx={cellStyle}>
                      {formatNum(item.price)}
                    </TableCell>

                    <TableCell sx={cellStyle}>{item.unit}</TableCell>

                    <TableCell align="right" sx={cellStyle}>
                      {formatNum(item.qty)}
                    </TableCell>

                    <TableCell align="right" sx={cellStyle}>
                      {formatNum(item.total)}
                    </TableCell>

                    <TableCell sx={cellStyle}>{item.batch}</TableCell>
                  </TableRow>
                ))}
              </TableBody>

              {/* FOOTER */}
              <TableFooter>
                <TableRow
                  sx={{
                    backgroundColor: "#fafafa",
                    "& td": {
                      fontWeight: "bold",
                    },
                  }}
                >
                  <TableCell sx={cellStyle} colSpan={4}>
                    รวมทั้งหมด
                  </TableCell>

                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.qty)}
                  </TableCell>

                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.total)}
                  </TableCell>

                  <TableCell sx={cellStyle} />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
